import React from "react";
import { Box } from "@mui/material";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import CustomDateRangePicker from "../common/CustomDateRangePicker";
import {
  CustomRange,
  DATE_RANGE_PRESETS,
  DateRangePreset,
} from "./keragonDateRange";

/**
 * "Result date range" chips for step 1 of the "Sync EHR data" modal (Figma
 * 16758:469482), with the since/until pickers that appear for "Custom"
 * (16761:470003). Resolution to request bounds lives in keragonDateRange.ts.
 */
type Props = {
  preset: DateRangePreset;
  onPresetChange: (preset: DateRangePreset) => void;
  custom: CustomRange;
  onCustomChange: (range: CustomRange) => void;
  disabled?: boolean;
};

const KeragonDateRangeChips = ({
  preset,
  onPresetChange,
  custom,
  onCustomChange,
  disabled = false,
}: Props) => {
  const theme: Theme = useTheme();

  return (
    <Box sx={styles.container}>
      {/* A field caption, not a heading: the section above already says
          "Choose system and date range", so this labels the chip group the
          way "System" labels the select. */}
      <Box sx={styles.caption(theme)}>Result date range</Box>
      <Box sx={styles.chips}>
        {DATE_RANGE_PRESETS.map((option) => {
          const selected = option.key === preset;
          return (
            <Box
              key={option.key}
              role="button"
              aria-pressed={selected}
              onClick={() => !disabled && onPresetChange(option.key)}
              sx={styles.chip(theme, selected, disabled)}
            >
              {option.label}
            </Box>
          );
        })}
      </Box>
      {preset === "custom" && (
        <Box sx={styles.customPicker}>
          <CustomDateRangePicker value={custom} onChange={onCustomChange} />
        </Box>
      )}
    </Box>
  );
};

export default KeragonDateRangeChips;

const styles = {
  container: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "8px",
  },
  caption: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "17px",
    color: theme.colorTokens.N[80],
  }),
  chips: {
    display: "flex",
    flexWrap: "wrap" as "wrap",
    gap: "8px",
  },
  // 12px like the rest of the modal body (Figma drew them at 14px, which read
  // oversized next to the 12px select value and captions).
  chip: (theme: Theme, selected: boolean, disabled: boolean) => ({
    padding: "6px 12px",
    borderRadius: "42px",
    border: `1px solid ${
      selected ? theme.colors_ds.PRIMARY_ICON_MAIN : theme.colorTokens.N[50]
    }`,
    backgroundColor: selected
      ? theme.colors_ds.PRIMARY_ICON_MAIN
      : theme.colors_ds.NEUTRAL_BG_MAIN,
    color: selected
      ? theme.colors_ds.NEUTRAL_GENERAL_WHITE
      : theme.colorTokens.N[90],
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "17px",
    cursor: disabled ? "default" : "pointer",
    opacity: disabled ? 0.6 : 1,
    userSelect: "none" as "none",
    "&:hover": {
      backgroundColor: selected
        ? theme.colorTokens.P[70]
        : theme.colorTokens.N[30],
    },
  }),
  customPicker: {
    marginTop: "8px",
  },
};
